import { Alert, Box, ScrollArea, Stack } from '@mantine/core';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import BulletStylesForm from 'src/secret-menu/export-assets/components/BulletStylesForm';
import ColorPaletteSection from 'src/secret-menu/export-assets/components/ColorPaletteSection';
import SectionHeader from 'src/secret-menu/export-assets/components/SectionHeader';
import TableStyleForm from 'src/secret-menu/export-assets/components/TableStyleForm';
import TypographySection from 'src/secret-menu/export-assets/components/TypographySection';
import { DEFAULT_CONFIG, type ExportBrandingConfigData } from 'src/secret-menu/export-assets/types';
import type { AutoBrandData } from 'src/secret-menu/auto-brand/types';

export default function ManualCorrectionStep(props: StepProps<AutoBrandData>) {
  const { data, setData, nextStep, previousStep } = props;

  const config = data.config ?? data.existingBranding?.config ?? DEFAULT_CONFIG;

  const handleConfigChange = (updated: ExportBrandingConfigData) => {
    setData(prev => ({ ...prev, config: updated }));
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Review the extracted branding"
      description="Fine-tune anything the extractor got wrong. Everything here will be applied to the tenant's export assets when you save."
      continueText="Looks good, continue"
      showClientName={false}
      hideProgress
      allowVerticalOverflow
      previousStep={() => previousStep()}
      nextStep={() => nextStep()}
    >
      <ScrollArea h={600}>
        <Stack gap="xl" pr="md">
          {data.documentType && data.documentType !== 'unknown' && (
            <Alert color="green" variant="light" title="Source detected">
              {data.documentType === 'brand_guidelines_pack'
                ? 'We detected a brand guidelines pack. Colours and logo placements were pulled directly from the pack.'
                : "We detected a branded example document. Typography and tables were read from the document's styles."}
            </Alert>
          )}

          <ColorPaletteSection config={config} onChange={handleConfigChange} />

          <TypographySection config={config} onChange={handleConfigChange} />

          <Box>
            <SectionHeader
              title="Table Style"
              subtitle="Configure the AfternoonTable style used for all exported tables."
            />
            <TableStyleForm config={config} onChange={handleConfigChange} />
          </Box>

          <Box>
            <SectionHeader
              title="Bullets"
              subtitle="Choose the bullet character for each indentation level."
            />
            <BulletStylesForm config={config} onChange={handleConfigChange} />
          </Box>
        </Stack>
      </ScrollArea>
    </DrawerFlowScreenLayout>
  );
}
